import { ArrowUpCircle } from "lucide-react";
import { Link } from "react-router";

import type { PatientResponseDto } from "~/lib/api";

interface EscalatedItem {
  patientId: string;
  patientName: string;
  bedNumber: string;
  description: string;
}

/** Flatten patients → their escalated, still-open tasks (escalatedAt set by HAN-79). */
function collectEscalated(patients: PatientResponseDto[]): EscalatedItem[] {
  const items: EscalatedItem[] = [];
  for (const p of patients) {
    // An acknowledged patient is signed-off; its tasks are no longer actionable,
    // so they never count here even if escalated_at was stamped earlier — same
    // posture as the overdue banner.
    if (p.acknowledgedAt != null) continue;
    for (const t of p.tasks) {
      if (t.completedAt == null && t.escalatedAt != null) {
        items.push({
          patientId: p.id,
          patientName: p.fullName,
          bedNumber: p.bedNumber,
          description: t.description,
        });
      }
    }
  }
  return items;
}

/**
 * HAN-79 (V2-E5.2) top-of-screen banner listing tasks escalated to a senior
 * clinician across the session's patients, each linking to its patient. Renders
 * nothing when there are none. Amber + role="status" — serious and sticky, but
 * deliberately distinct from the red, assertive overdue banner so the two read
 * as different states.
 */
export function EscalatedTasksBanner({
  sessionId,
  patients,
}: {
  sessionId: string;
  patients: PatientResponseDto[];
}): JSX.Element | null {
  const escalated = collectEscalated(patients);
  if (escalated.length === 0) return null;

  return (
    <div
      role="status"
      aria-label={`${escalated.length} task${escalated.length > 1 ? "s" : ""} escalated to a senior`}
      data-testid="escalated-tasks-banner"
      className="sticky top-0 z-10 -mx-4 border-b border-amber-600 bg-amber-500 px-4 py-3 text-sm text-white sm:-mx-6 sm:px-6"
    >
      <div className="flex items-center gap-2 font-semibold">
        <ArrowUpCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
        {escalated.length} task{escalated.length > 1 ? "s" : ""} escalated to a senior
      </div>
      <ul className="mt-1.5 space-y-1">
        {escalated.map((item, i) => (
          <li key={`${item.patientId}-${i}`}>
            <Link
              to={`/session/${sessionId}/patient/${item.patientId}`}
              className="underline-offset-2 hover:underline"
            >
              {item.patientName}, bed {item.bedNumber} — {item.description}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
